import { ICollaboration } from '@descomplica/microfrontend-core';
import { injectable } from 'tsyringe';

import { ClassroomDto } from '@/application/interfaces/classroom/dtos/get-classroom-by-topic';
import { KalturaGateway } from '@/infrastructure/gateways/classroom/kaltura.gateway';
import { CollaborationResult } from '@/infrastructure/gateways/classroom/models/collaboration-result';
import { PegasusGateway } from '@/infrastructure/gateways/classroom/pegasus.gateway';
import { Classroom } from '@/presentation/screens/classroom/classroom.vm';

import {
  ObtainedClassroom,
  UnavailableService,
} from './get-classroom-by-course.collaboration.responses';

interface Params {
  guid: string;
}

export const PEGASUS_IDENTIFIERS = {
  GetClassroomServiceError: 'GetClassroomServiceError',
  ClassroomLessonsNotFound: 'ClassroomLessonsNotFound',
  ClassroomDisciplineNotFound: 'ClassroomDisciplineNotFound',
  ClassroomDisciplineTopicsNotFound: 'ClassroomDisciplineTopicsNotFound',
  ClassroomDisciplineSubjectsNotFound: 'ClassroomDisciplineSubjectsNotFound',
  RequiredTopicSlugNotSentError: 'RequiredTopicSlugNotSentError',
  UserNotIdentifiedError: 'UserNotIdentifiedError',
  UnauthorizedStudentBecauseNotIdentified: 'UnauthorizedStudentBecauseNotIdentified',
};

@injectable()
export class GetClassroomByCourseCollaboration
  implements ICollaboration<Params, Promise<CollaborationResult<Classroom>>>
{
  constructor(private pegasusGateway: PegasusGateway, private kalturaGateway: KalturaGateway) {}

  async run({ guid }: Params): Promise<CollaborationResult<Classroom>> {
    const { data, error } = await this.pegasusGateway.getClassroomByCourseGuid(guid);

    if (data && !error) {
      return new ObtainedClassroom(this.serealizeToDataScreen(data));
    }

    return new UnavailableService();
  }

  private getCourseWithMatchingProgress(data: ClassroomDto) {
    return {
      ...data.course,
      lessons: data.course.lessons?.map((lesson) => {
        const lessonProgress = data.lessonsProgress.find((lp) => lp.lesson_guid === lesson.guid);
        return {
          ...lesson,
          videos: lesson.videos?.map((video) => {
            const videoProgress = lessonProgress?.videosProgress?.find(
              (vp) => vp.video_guid === video.guid,
            );

            return {
              ...video,
              progress: videoProgress?.usage,
              thumbUrl: this.kalturaGateway.getRandomVideoThumbnailUrl(video.kalturaId),
              isCompleted: videoProgress?.isComplete,
            };
          }),
          isCompleted: lessonProgress?.isComplete,
        };
      }),
      lessonsProgress: data.lessonsProgress,
    } as any;
  }

  private serealizeToDataScreen(data: ClassroomDto): Classroom {
    const serealizedCourse = this.getCourseWithMatchingProgress(data);

    return {
      course: serealizedCourse,
      currentVideoGuid: data.currentVideo,
      studentRegistrationCode: data.studentRegistrationCode,
    };
  }
}
